import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Search,
  Loader2,
  UserCircle2,
  ShieldCheck,
  AlertCircle,
  Bot,
  Copy,
  Users,
  Mail,
  IdCard,
  Globe,
  Shield,
  GitCompare,
} from 'lucide-react';
import { getApiBase } from './apiBase';
import UserLookupSecuritySection from './AiCopilot/UserLookupSecuritySection';
import CollapsibleStrip from './AiCopilot/CollapsibleStrip';
import SodBulkScan from './SodBulkScan';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 60000,
});

function relTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

export default function UserLookup({
  onAskCopilot,
  /** Set when navigating from SQL Studio access-map results */
  focusUsername: focusUsernameFromNav = '',
  onFocusUsernameConsumed,
}) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // worker search (separate, type-ahead-ish)
  const [nameQuery, setNameQuery] = useState('');
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersError, setWorkersError] = useState('');
  const [workersSearched, setWorkersSearched] = useState(false);
  const [pickedWorker, setPickedWorker] = useState(null);
  const debounceRef = useRef(null);
  const [lookupTab, setLookupTab] = useState('security');

  const LOOKUP_TABS = [
    { id: 'security', label: 'User security', icon: Shield },
    { id: 'compare', label: 'Compare users', icon: GitCompare },
    { id: 'sod-bulk', label: 'SoD bulk scan', icon: ShieldCheck },
  ];

  useEffect(() => {
    if (focusUsernameFromNav) {
      setQuery(focusUsernameFromNav);
      runLookup(focusUsernameFromNav);
      setLookupTab('security');
      if (typeof onFocusUsernameConsumed === 'function') onFocusUsernameConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUsernameFromNav]);

  async function runLookup(q) {
    setSubmitted(q);
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await API.get('/api/hcm/users/lookup', {
        params: { q },
        validateStatus: () => true,
      });
      if (res.status === 200) {
        setResult(res.data);
      } else {
        setError(res.data?.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    runLookup(q);
  }

  function clearLookup() {
    setSubmitted('');
    setResult(null);
    setError('');
    setQuery('');
    setNameQuery('');
    setWorkers([]);
    setWorkersError('');
    setWorkersSearched(false);
    setPickedWorker(null);
  }

  async function runWorkerSearch(qRaw) {
    const q = String(qRaw ?? nameQuery).trim();
    if (q.length < 2) {
      setWorkers([]);
      setWorkersError('Type at least 2 characters, then click Find workers or keep typing.');
      setWorkersSearched(false);
      return;
    }
    setWorkersLoading(true);
    setWorkersError('');
    setWorkersSearched(false);
    try {
      const res = await API.get('/api/hcm/workers/search', {
        params: { q },
        validateStatus: () => true,
      });
      if (res.status === 200) {
        const list = res.data.workers || [];
        setWorkers(list);
        setWorkersSearched(true);
        if (list.length === 0) {
          setWorkersError(res.data.message || `No workers matched "${q}".`);
        }
      } else if (res.status === 503) {
        setWorkers([]);
        setWorkersSearched(true);
        setWorkersError(res.data?.error || 'No active HCM connection — configure a tenant first.');
      } else {
        setWorkers([]);
        setWorkersSearched(true);
        setWorkersError(res.data?.error || res.data?.message || `Search failed (HTTP ${res.status}).`);
      }
    } catch (err) {
      setWorkers([]);
      setWorkersSearched(true);
      setWorkersError(err.message || 'Worker search failed');
    } finally {
      setWorkersLoading(false);
    }
  }

  const hasLookupData = !!(submitted || result || error);

  // type-ahead worker search (debounced; explicit button uses runWorkerSearch)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = nameQuery.trim();
    if (q.length < 2) {
      setWorkers([]);
      setWorkersError('');
      setWorkersSearched(false);
      return;
    }
    debounceRef.current = setTimeout(() => runWorkerSearch(q), 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce nameQuery only
  }, [nameQuery]);

  function pickWorker(w) {
    const pn = w.personNumber ? String(w.personNumber).trim() : '';
    const un = w.username ? String(w.username).trim() : '';
    const lookupKey = un || pn;
    setPickedWorker({ ...w, personNumber: pn, username: un });
    setNameQuery('');
    setWorkers([]);
    setWorkersError('');
    setWorkersSearched(false);
    if (!lookupKey) {
      setWorkersError('This worker has no person number or username in the directory — try another match.');
      return;
    }
    setQuery(lookupKey);
    runLookup(lookupKey);
  }

  function copy(text) {
    if (navigator?.clipboard) navigator.clipboard.writeText(String(text));
  }

  function handoffToCopilot(prefilledPrompt) {
    if (typeof onAskCopilot === 'function') {
      onAskCopilot({
        feature: 'security',
        prompt: prefilledPrompt,
        context: {
          user: result?.user,
          roles: result?.roles?.map((r) => r.roleCode),
        },
      });
    }
  }

  const found = result?.found;
  const user = result?.user;
  const roles = result?.roles || [];

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold text-slate-800">User Lookup</h2>

      {/* Search by name (find a PersonNumber) */}
      <CollapsibleStrip
        storageKey="aiTool.userLookup.workerSearch"
        title="Search workers by name"
        collapsedSummary={nameQuery.trim() ? nameQuery.trim() : 'By display name'}
        className="rounded-xl border border-slate-200 bg-white"
      >
        <div className="p-4">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
          <Users className="h-4 w-4 text-slate-500" />
          Type a name, then pick a worker
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1">
          <input
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runWorkerSearch();
              }
            }}
            placeholder='e.g. "Jane Doe" or "Savage"'
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pl-9 pr-9 text-sm text-slate-800 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          {workersLoading && (
            <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-slate-400" />
          )}
          </div>
          <button
            type="button"
            onClick={() => runWorkerSearch()}
            disabled={workersLoading || nameQuery.trim().length < 2}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
          >
            {workersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find workers
          </button>
        </div>
        {workersError && (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{workersError}</span>
          </p>
        )}
        {workersSearched && !workersLoading && workers.length === 0 && !workersError && (
          <p className="mt-2 text-xs text-slate-500">No matches — try last name or a different spelling.</p>
        )}
        {workers.length > 0 && (
          <ul className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            {workers.map((w) => {
              const pn = w.personNumber ? String(w.personNumber).trim() : '';
              const un = w.username ? String(w.username).trim() : '';
              const key = w.personId || pn || un || w.displayName;
              return (
                <li key={key} className="border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    onClick={() => pickWorker(w)}
                    className="flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm hover:bg-cyan-50"
                  >
                    <UserCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-800">
                        {w.displayName || `${w.firstName || ''} ${w.lastName || ''}`.trim() || '—'}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-md px-2 py-0.5 font-mono text-xs font-semibold ${
                            pn ? 'bg-cyan-100 text-cyan-900' : 'bg-amber-100 text-amber-900'
                          }`}
                        >
                          {pn ? `#${pn}` : 'No person #'}
                        </span>
                        {un && (
                          <span className="truncate font-mono text-xs text-slate-600">{un}</span>
                        )}
                        {w.workEmail && (
                          <span className="truncate text-xs text-slate-500">{w.workEmail}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {pickedWorker && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50/80 px-3 py-2 text-sm text-slate-800">
            <span className="font-medium">Selected:</span>
            <span>{pickedWorker.displayName || pickedWorker.username || pickedWorker.personNumber}</span>
            {pickedWorker.personNumber && (
              <span className="font-mono font-semibold text-cyan-900">#{pickedWorker.personNumber}</span>
            )}
            {pickedWorker.username && (
              <span className="font-mono text-slate-600">({pickedWorker.username})</span>
            )}
            {pickedWorker.personNumber && (
              <button
                type="button"
                onClick={() => copy(pickedWorker.personNumber)}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-cyan-300 bg-white px-2 py-0.5 text-xs font-medium text-cyan-800 hover:bg-cyan-50"
              >
                <Copy className="h-3 w-3" />
                Copy person #
              </button>
            )}
          </div>
        )}
        <p className="mt-2 text-xs text-slate-500">
          Directory search uses Fusion <code className="rounded bg-slate-100 px-1">publicWorkers</code> (name →
          person number / username). Pick a row to run account lookup below.
        </p>
        </div>
      </CollapsibleStrip>

      {/* Primary lookup form */}
      <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
          <Search className="h-4 w-4 text-slate-500" />
          Lookup user account &amp; roles
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Username (case-sensitive), PersonNumber, or work email"
            className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="flex items-center justify-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Lookup
          </button>
          <button
            type="button"
            onClick={clearLookup}
            disabled={loading || !hasLookupData}
            className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </form>

      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        {LOOKUP_TABS.map(({ id, label, icon: Icon }) => {
          const active = lookupTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setLookupTab(id)}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-white text-cyan-800 shadow-sm ring-1 ring-slate-200/80'
                  : 'text-slate-600 hover:bg-white/60 hover:text-slate-800'
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? 'text-cyan-600' : 'text-slate-400'}`} />
              {label}
            </button>
          );
        })}
      </div>

      {lookupTab === 'sod-bulk' && <SodBulkScan />}

      {lookupTab !== 'sod-bulk' && (
      <UserLookupSecuritySection
        panel={lookupTab}
        focusUsername={(found && user ? user.username : '') || focusUsernameFromNav}
        scimRoles={found && roles?.length ? roles : null}
        scimUserMeta={
          found && user
            ? { displayName: user.displayName, personNumber: user.personNumber }
            : null
        }
      />
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="break-words">{error}</div>
        </div>
      )}

      {result && !found && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
          <UserCircle2 className="mx-auto mb-2 h-10 w-10 text-slate-300" />
          <div className="text-sm">No user found for "{submitted}".</div>
          <div className="mt-1 text-xs">{result.message}</div>
        </div>
      )}

      {found && user && (
        <div className="space-y-4">
          {/* User card */}
          <CollapsibleStrip
            storageKey="aiTool.userLookup.scimCard"
            title="SCIM user account"
            collapsedSummary={
              user.displayName || user.username
                ? `${user.displayName || user.username} · ${user.username}`
                : user.username
            }
            className="rounded-xl border border-slate-200 bg-white"
          >
            <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700">
                <UserCircle2 className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="truncate text-lg font-semibold text-slate-800">
                    {user.displayName || user.username}
                  </div>
                  {user.active ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                      Inactive
                    </span>
                  )}
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    matched by {result.matchedBy}
                  </span>
                </div>
                <div className="mt-0.5 text-sm text-slate-500">
                  <span className="font-mono">{user.username}</span>
                  {user.email && <span> · {user.email}</span>}
                </div>
                <div className="mt-2 grid gap-1 text-xs text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
                  {user.scimId && (
                    <span className="flex items-center gap-1">
                      <IdCard className="h-3 w-3" />
                      <span className="font-mono">{user.scimId}</span>
                    </span>
                  )}
                  {user.personNumber && (
                    <span className="flex items-center gap-1">
                      <IdCard className="h-3 w-3" />
                      PersonNumber {user.personNumber}
                    </span>
                  )}
                  {user.preferredLanguage && (
                    <span className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {user.preferredLanguage}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Created {relTime(user.createdAt)?.slice(0, 10)}
                  </span>
                </div>
              </div>
            </div>
            </div>
          </CollapsibleStrip>

          {/* Roles table */}
          <CollapsibleStrip
            storageKey="aiTool.userLookup.scimRoles"
            title="Effective roles (SCIM)"
            collapsedSummary={`${roles.length} role${roles.length === 1 ? '' : 's'}`}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white"
            headerClassName="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2"
            titleClassName="text-sm font-semibold text-slate-700"
            headerActions={
              onAskCopilot && roles.length > 0 ? (
                <button
                  type="button"
                  onClick={() =>
                    handoffToCopilot(
                      `Walk me through the access ${user.displayName || user.username} has based on these ${roles.length} roles: ${roles
                        .map((r) => `${r.displayName} (${r.roleCode})`)
                        .join(
                          '; '
                        )}. What HCM functions can they perform, what data security do they get, and are there any obvious gaps a typical HR specialist would still need?`
                    )
                  }
                  className="flex items-center gap-1.5 rounded-md border border-cyan-200 bg-white px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50"
                >
                  <Bot className="h-3.5 w-3.5" />
                  Ask Security Copilot
                </button>
              ) : null
            }
          >
            {roles.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No roles assigned.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Role</th>
                    <th className="px-4 py-2 text-left font-semibold">Role code</th>
                    <th className="px-4 py-2 text-right font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {roles.map((r) => (
                    <tr key={r.scimRoleId || r.roleCode}>
                      <td className="px-4 py-2 align-top">
                        <div className="font-medium text-slate-800">{r.displayName}</div>
                        {r.description && (
                          <div className="mt-0.5 text-xs text-slate-500">{r.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 align-top">
                        <div className="font-mono text-[11px] text-slate-600">{r.roleCode}</div>
                      </td>
                      <td className="px-4 py-2 text-right align-top">
                        <button
                          type="button"
                          onClick={() => copy(r.roleCode)}
                          className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:bg-slate-50"
                          title="Copy role code"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleStrip>

          {result.connection && (
            <div className="text-xs text-slate-400">
              Via SCIM on{' '}
              {result.connection.source === 'db'
                ? `saved connection "${result.connection.name}"`
                : '.env fallback'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
